import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import { Redirect, Route, Switch, useLocation } from "wouter";
import { useEffect } from "react";
import ErrorBoundary from "./components/ErrorBoundary";
import CookieBanner from "./components/public/CookieBanner";
import { ThemeProvider } from "./contexts/ThemeContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import Home from "./pages/Home";
import Equipo from "./pages/Equipo";
import PrivacyPage from "./pages/legal/Privacy";
import LegalPage from "./pages/legal/Legal";
import CookiesPage from "./pages/legal/Cookies";
import AdminPanel from "./pages/admin/AdminPanel";
import AdminPlayers from "./pages/admin/AdminPlayers";
import AdminPlayerDetail from "./pages/admin/AdminPlayerDetail";
import AdminReportEditor from "./pages/admin/AdminReportEditor";
import AdminCoaches from "./pages/admin/AdminCoaches";
import AdminContacts from "./pages/admin/AdminContacts";

/**
 * NOTE: Player-facing private routes (/mi-cuenta, /mi-informe, /mi-evolucion, /informe-muestra)
 * are intentionally disabled while the metric-driven report editor is being redesigned.
 * They redirect to "/" to avoid exposing unfinished flows. Admin panel remains reachable
 * by direct URL for internal use but has no visible link from the public site.
 */

function ScrollToTop() {
  const [location] = useLocation();
  useEffect(() => {
    // Do not interfere when navigating to an in-page anchor (#section).
    if (window.location.hash) return;
    window.scrollTo(0, 0);
  }, [location]);
  return null;
}

function Router() {
  return (
    <Switch>
      {/* Public site */}
      <Route path="/" component={Home} />
      <Route path="/equipo" component={Equipo} />
      <Route path="/privacidad" component={PrivacyPage} />
      <Route path="/aviso-legal" component={LegalPage} />
      <Route path="/cookies" component={CookiesPage} />

      {/* Player zone — TEMPORARILY DISABLED (stand-by) */}
      <Route path="/mi-cuenta">{() => <Redirect to="/" />}</Route>
      <Route path="/mi-cuenta/informe/:id">{() => <Redirect to="/" />}</Route>
      <Route path="/mi-cuenta/evolucion">{() => <Redirect to="/" />}</Route>
      <Route path="/mi-informe">{() => <Redirect to="/" />}</Route>
      <Route path="/mi-informe/:id">{() => <Redirect to="/" />}</Route>
      <Route path="/mi-evolucion">{() => <Redirect to="/" />}</Route>
      <Route path="/informe-muestra">{() => <Redirect to="/" />}</Route>

      {/* Admin zone (direct URL only, no public link — requires admin role) */}
      <Route path="/admin" component={AdminPanel} />
      <Route path="/admin/jugadores" component={AdminPlayers} />
      <Route path="/admin/jugadores/:id" component={AdminPlayerDetail} />
      <Route path="/admin/jugadores/:playerId/informes/nuevo" component={AdminReportEditor} />
      <Route path="/admin/informes/:id" component={AdminReportEditor} />
      <Route path="/admin/equipo" component={AdminCoaches} />
      <Route path="/admin/contactos" component={AdminContacts} />

      <Route path="/404" component={NotFound} />
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light">
        <LanguageProvider>
          <TooltipProvider>
            <Toaster />
            <ScrollToTop />
            <Router />
            <CookieBanner />
          </TooltipProvider>
        </LanguageProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
